import { prettyError } from '@grin-rnd/grin-api-sdk/dist/utils/ErrorUtils'
import { CopilotEngagementCategories } from 'consts/copilotControlConsts'
import { NotifyPracticeReasons } from 'consts/giConsts'
import * as copilotControl from 'graphql/copilotControl.graphql'
import { practiceGuidelinesByDoctorIdSorted } from 'graphql/customQueries'
import { api, REQUEST_TYPE } from 'store/api'
import { logError } from 'utils/logUtils'
import { ModuleTags } from './consts'
import { removeFromPaginatedGraphQueryState } from 'utils/rtkUtils'
import { StatusTypes } from '@grin-rnd/grin-api-sdk/dist/Enums/Statuses'

const mapPatientTagToNeedsAttentionData = patientTag => {
  const { metadata, createdAt } = patientTag
  const { reason, note, selectedPracticeMembersIds } = JSON.parse(metadata || '{}')

  return {
    createdAt,
    reason: reason || NotifyPracticeReasons.Other,
    note,
    selectedPracticeMembersIds
  }
}

const mapSentimentCategoryToScoreFilter = categories => {
  let filter = []

  if (categories.includes(CopilotEngagementCategories.Negative)) {
    filter.push({ copilotChatSentimentScore: { lte: '2' } })
  }
  if (categories.includes(CopilotEngagementCategories.Neutral)) {
    filter.push({ copilotChatSentimentScore: { eq: '3' } })
  }
  if (categories.includes(CopilotEngagementCategories.Positive)) {
    filter.push({ copilotChatSentimentScore: { gte: '4' } })
  }

  return filter
}

const SEARCH_PATIENTS_PAGE_SIZE = 12

export const CopilotControl = api.injectEndpoints({
  endpoints: build => ({
    fetchCopilotMetabaseData: build.query({
      query: ({ accountOwnerId, pastXDays }) => ({
        type: REQUEST_TYPE.GET,
        path: `/copilot/v1/practices/${accountOwnerId}/metabase`,
        queryParams: {
          pastXDays
        }
      }),
      providesTags: [ModuleTags.CopilotControlMetabase]
    }),
    fetchPatientsSentimentAggregation: build.query({
      query: ({ accountOwnerId }) => ({
        type: REQUEST_TYPE.GET,
        path: `/copilot/v1/practices/${accountOwnerId}/aggregations/patientsSentiment`
      }),
      providesTags: [ModuleTags.CopilotControlSentiment]
    }),
    fetchPatientsSentimentData: build.infiniteQuery({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages, lastPageParam) => lastPageParam + 1
      },
      keepUnusedDataFor: 0,
      query: ({ queryArg, pageParam }) => {
        const { accountOwnerId, categories = [] } = queryArg
        const filter = {
          copilotChatSentimentScore: { exists: true },
          doctorId: { eq: accountOwnerId },
          and: [
            {
              or: [
                { statusType: { eq: StatusTypes.ActiveTreatment } },
                { statusType: { eq: StatusTypes.Observation } },
                { statusType: { eq: StatusTypes.Retention } },
                { statusType: { eq: StatusTypes.NewLead } }
              ]
            }
          ]
        }

        if (categories.length) {
          filter.and.push({
            or: mapSentimentCategoryToScoreFilter(categories)
          })
        }
        return {
          type: REQUEST_TYPE.GRAPHQL,
          query: copilotControl.searchPatientSearchModels(copilotControl.fieldsByQuery.PatientsSentimentQuery),
          queryName: 'searchPatientSearchModels',
          variables: {
            limit: SEARCH_PATIENTS_PAGE_SIZE,
            from: pageParam * SEARCH_PATIENTS_PAGE_SIZE,
            sort: { direction: 'desc', field: 'lastMessageTimestamp' },
            filter
          }
        }
      },
      transformResponse: res => ({
        items: (res?.searchPatientSearchModels?.items || []).map(psm => {
          return {
            ...psm,
            copilotChatSentimentDescription:
              JSON.parse(psm.patient?.copilotData || '{}').chatAnalysis?.sentimentReason || ''
          }
        }),
        hasMore: res?.searchPatientSearchModels?.items.length === SEARCH_PATIENTS_PAGE_SIZE
      })
    }),
    fetchNeedsAttentionAggregation: build.query({
      query: ({ accountOwnerId }) => ({
        type: REQUEST_TYPE.GET,
        path: `/copilot/v1/practices/${accountOwnerId}/aggregations/needsAttention`
      }),
      providesTags: [ModuleTags.CopilotNeedsAttention]
    }),
    fetchScanningScheduleAggregation: build.query({
      query: ({ accountOwnerId }) => ({
        type: REQUEST_TYPE.GET,
        path: `/copilot/v1/practices/${accountOwnerId}/aggregations/scanningSchedule`
      }),
      providesTags: [ModuleTags.CopilotSchedulingScore]
    }),
    fetchNeedsAttentionPatients: build.infiniteQuery({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages, lastPageParam) => lastPageParam + 1
      },
      keepUnusedDataFor: 0,
      query: ({ queryArg, pageParam }) => {
        const { accountOwnerId, selectedReasons } = queryArg
        const filter = {
          doctorId: { eq: accountOwnerId },
          and: [
            {
              or: [
                { statusType: { eq: StatusTypes.ActiveTreatment } },
                { statusType: { eq: StatusTypes.Observation } },
                { statusType: { eq: StatusTypes.Retention } },
                { statusType: { eq: StatusTypes.NewLead } }
              ],
              needsAttentionReason: {
                exists: true
              }
            }
          ]
        }

        if (selectedReasons.length) {
          filter.and.push({
            or: selectedReasons.map(reason => ({ needsAttentionReason: { eq: reason } }))
          })
        }

        return {
          type: REQUEST_TYPE.GRAPHQL,
          query: copilotControl.searchPatientSearchModels(copilotControl.fieldsByQuery.NeedsAttentionPatientsQuery),
          queryName: 'searchPatientSearchModels',
          variables: {
            limit: SEARCH_PATIENTS_PAGE_SIZE,
            from: pageParam * SEARCH_PATIENTS_PAGE_SIZE,
            filter,
            sort: {
              direction: 'desc',
              field: 'needsAttentionTime'
            }
          }
        }
      },
      transformResponse: res => ({
        items: (res?.searchPatientSearchModels?.items || []).map(psm => {
          const needsAttentionTag = psm.patientTags.items.find(
            patientTag => patientTag.tag?.value === 'needsAttention' && !patientTag._deleted
          )
          return {
            ...psm,
            needsAttentionData: needsAttentionTag ? mapPatientTagToNeedsAttentionData(needsAttentionTag) : null
          }
        }),
        hasMore: res?.searchPatientSearchModels?.items.length === SEARCH_PATIENTS_PAGE_SIZE
      })
    }),
    resolveNeedsAttention: build.mutation({
      query: ({ patientId }) => ({
        type: REQUEST_TYPE.POST,
        path: `/treatments/v2/gi/notifyPractice/resolve`,
        data: {
          patientId
        }
      }),
      onQueryStarted: async (
        { patientId, needsAttentionReason, accountOwnerId },
        { dispatch, queryFulfilled, getState }
      ) => {
        const state = getState()
        const selectedReasons = state.copilotControlReducer.selectedNeedsAttentionReasons || []

        const fetchNeedsAttentionPatients = dispatch(
          api.util.updateQueryData('fetchNeedsAttentionPatients', { accountOwnerId, selectedReasons }, patients => {
            removeFromPaginatedGraphQueryState({
              pages: patients.pages,
              predicate: patient => patient.id === patientId
            })
          })
        )
        const fetchNeedsAttentionAggregationPatch = dispatch(
          api.util.updateQueryData('fetchNeedsAttentionAggregation', { accountOwnerId }, aggregation => {
            aggregation[needsAttentionReason] -= 1
          })
        )

        try {
          await queryFulfilled
        } catch (error) {
          fetchNeedsAttentionPatients.undo()
          fetchNeedsAttentionAggregationPatch.undo()
          logError('copilotResolveNeedsAttention: An error occured while trying to resolve patient needs attenntion', {
            patientId,
            error: prettyError(error)
          })
        }
      }
    }),
    fetchScanningSchedulePatients: build.infiniteQuery({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages, lastPageParam) => lastPageParam + 1
      },
      keepUnusedDataFor: 0,
      query: ({ queryArg, pageParam }) => {
        const { accountOwnerId, categories = [] } = queryArg
        const filter = {
          and: [
            {
              copilotScanningScheduleScore: { exists: true },
              copilotScanningScheduleScoreCategory: { exists: true },
              doctorId: { eq: accountOwnerId },
              or: [
                { statusType: { eq: StatusTypes.ActiveTreatment } },
                { statusType: { eq: StatusTypes.Observation } },
                { statusType: { eq: StatusTypes.Retention } },
                { statusType: { eq: StatusTypes.NewLead } }
              ]
            }
          ]
        }

        if (categories.length) {
          filter.and.push({
            or: categories.map(category => ({
              copilotScanningScheduleScoreCategory: { eq: category }
            }))
          })
        }

        return {
          type: REQUEST_TYPE.GRAPHQL,
          query: copilotControl.searchPatientSearchModels(copilotControl.fieldsByQuery.PatientsScanningScoreQuery),
          queryName: 'searchPatientSearchModels',
          variables: {
            limit: SEARCH_PATIENTS_PAGE_SIZE,
            from: pageParam * SEARCH_PATIENTS_PAGE_SIZE,
            sort: { direction: 'desc', field: 'lastGrinScanUploadDate' },
            filter
          }
        }
      },
      transformResponse: res => ({
        items: res?.searchPatientSearchModels?.items || [],
        hasMore: res?.searchPatientSearchModels?.items.length === SEARCH_PATIENTS_PAGE_SIZE
      })
    }),
    fetchLatestPracticeGuidelines: build.query({
      query: ({ accountOwnerId }) => ({
        type: REQUEST_TYPE.GRAPHQL,
        query: practiceGuidelinesByDoctorIdSorted,
        variables: {
          sortDirection: 'DESC',
          limit: 1,
          doctorId: accountOwnerId
        }
      }),
      transformResponse: res => res.practiceGuidelinesByDoctorIdSorted?.items[0],
      providesTags: [ModuleTags.PracticeGuidelines]
    }),
    fetchPatientCopilotData: build.query({
      query: ({ patientId }) => ({
        type: REQUEST_TYPE.GRAPHQL,
        query: copilotControl.fetchPatientCopilotData,
        variables: { patientId }
      }),
      providesTags: [ModuleTags.PatientCopilot],
      transformResponse: res => {
        const copilotData = JSON.parse(res?.getPatient?.copilotData || '{}')
        const timelineItems = res.searchTimelineItems?.items.reverse().map(timelineItem => ({
          ...timelineItem,
          payload: JSON.parse(timelineItem.payload || '{}')
        }))

        return {
          copilotData,
          timelineItems
        }
      }
    })
  })
})

export const {
  useFetchCopilotMetabaseDataQuery,
  useFetchNeedsAttentionPatientsInfiniteQuery,
  useFetchPatientsSentimentAggregationQuery,
  useFetchPatientsSentimentDataInfiniteQuery,
  useFetchLatestPracticeGuidelinesQuery,
  useFetchNeedsAttentionAggregationQuery,
  useFetchPatientCopilotDataQuery,
  useFetchScanningScheduleAggregationQuery,
  useFetchScanningSchedulePatientsInfiniteQuery,
  useResolveNeedsAttentionMutation
} = CopilotControl
